/**
 * AWARE Health — What's New Drawer
 * Slide-out panel showing changelog and upcoming features.
 *
 * - Rendered through a React portal into <body>. The header uses
 *   `backdrop-filter`, which turns it into the containing block for
 *   `position: fixed` children — without the portal the drawer was
 *   clipped inside the 56px header and never visible.
 * - Content comes from the locale files, so it is fully translated.
 * - Unread badge tracks the newest entry date in localStorage; entries
 *   newer than the last visit get a "New" chip.
 * - Closes on backdrop click or the Escape key.
 */

import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useTheme } from '../../contexts/ThemeContext'

interface ChangelogEntry {
  date: string
  title: string
  description: string
  bullets?: string[]
}

interface UpcomingEntry {
  title: string
  description: string
}

const LAST_SEEN_KEY = 'aware-whats-new-last-seen'
const EPOCH = '2000-01-01'

function asArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : []
}

export default function WhatsNew() {
  const { t, i18n } = useTranslation()
  const { theme } = useTheme()
  const isDark = theme === 'dark'

  const entries = asArray<ChangelogEntry>(t('whatsNew.entries', { returnObjects: true }))
  const upcoming = asArray<UpcomingEntry>(t('whatsNew.upcoming', { returnObjects: true }))
  const latestDate = entries.reduce((max, e) => (e.date > max ? e.date : max), EPOCH)

  const [open, setOpen] = useState(false)
  const [lastSeen, setLastSeen] = useState(() => localStorage.getItem(LAST_SEEN_KEY) ?? EPOCH)
  // Snapshot of lastSeen taken when the drawer opens, so "New" chips stay visible while reading
  const [highlightAfter, setHighlightAfter] = useState(lastSeen)

  const unreadCount = entries.filter((e) => e.date > lastSeen).length

  function handleOpen() {
    setHighlightAfter(lastSeen)
    setOpen(true)
    localStorage.setItem(LAST_SEEN_KEY, latestDate)
    setLastSeen(latestDate)
  }

  // Auto-open if there are unread entries
  useEffect(() => {
    if (unreadCount > 0) {
      handleOpen()
    }
  }, []) // run once on mount

  // Close on Escape
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  function formatDate(iso: string) {
    try {
      return new Date(`${iso}T00:00:00`).toLocaleDateString(i18n.resolvedLanguage ?? 'en', {
        year: 'numeric', month: 'short', day: 'numeric',
      })
    } catch {
      return iso
    }
  }

  // ── Palette ──────────────────────────────────────────────────────────
  const c = {
    panelBg:    isDark ? '#0a1220' : '#ffffff',
    panelBorder:isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
    title:      isDark ? '#f1f5f9' : '#0f172a',
    body:       isDark ? '#cbd5e1' : '#334155',
    muted:      isDark ? '#94a3b8' : '#64748b',
    faint:      isDark ? '#64748b' : '#94a3b8',
    cardBg:     isDark ? 'rgba(255,255,255,0.03)' : '#f8fafc',
    cardBorder: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)',
    accent:     isDark ? '#2dd4bf' : '#0d9488',
    accentBg:   isDark ? 'rgba(20,184,166,0.12)' : 'rgba(13,148,136,0.08)',
    accentBorder: isDark ? 'rgba(20,184,166,0.35)' : 'rgba(13,148,136,0.3)',
    blue:       isDark ? '#93c5fd' : '#2563eb',
    blueBg:     isDark ? 'rgba(37,99,235,0.08)' : 'rgba(37,99,235,0.04)',
    blueBorder: isDark ? 'rgba(96,165,250,0.25)' : 'rgba(37,99,235,0.2)',
  }

  const drawer = (
    <>
      {/* Backdrop */}
      <div
        onClick={() => setOpen(false)}
        style={{
          position: 'fixed',
          inset: 0,
          background: isDark ? 'rgba(0,0,0,0.55)' : 'rgba(15,23,42,0.25)',
          backdropFilter: 'blur(3px)',
          zIndex: 10000,
          animation: 'fadeIn 0.2s ease-out',
        }}
      />

      {/* Panel */}
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={t('whatsNew.title')}
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: 400,
          maxWidth: '92vw',
          background: c.panelBg,
          borderLeft: `1px solid ${c.panelBorder}`,
          boxShadow: isDark ? '-24px 0 60px rgba(0,0,0,0.55)' : '-24px 0 60px rgba(15,23,42,0.12)',
          zIndex: 10001,
          display: 'flex',
          flexDirection: 'column',
          fontFamily: 'Inter, system-ui, sans-serif',
          animation: 'slideInRight 0.28s cubic-bezier(0.22, 1, 0.36, 1)',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '18px 20px',
            borderBottom: `1px solid ${c.panelBorder}`,
            flexShrink: 0,
          }}
        >
          <div
            style={{
              width: 38, height: 38, borderRadius: 10, flexShrink: 0,
              background: 'linear-gradient(135deg, #0d9488, #2563eb)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 18, boxShadow: '0 6px 16px rgba(13,148,136,0.35)',
            }}
          >
            ✨
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: c.title }}>
              {t('whatsNew.title')}
            </h2>
            <p style={{ margin: '2px 0 0', fontSize: 12, color: c.muted }}>
              {t('whatsNew.subtitle')}
            </p>
          </div>
          <button
            onClick={() => setOpen(false)}
            aria-label={t('whatsNew.close')}
            title={t('whatsNew.close')}
            style={{
              width: 30, height: 30, borderRadius: 8, border: 'none', flexShrink: 0,
              background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
              color: c.muted, cursor: 'pointer', fontSize: 18, lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>

        {/* Scrollable content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '18px 20px 28px' }}>
          {/* Changelog timeline */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {entries.map((entry, i) => {
              const isNew = entry.date > highlightAfter
              const isFeatured = i === 0
              return (
                <div
                  key={`${entry.date}-${i}`}
                  className="animate-fade-in-up"
                  style={{
                    animationDelay: `${i * 40}ms`,
                    padding: '14px 16px',
                    borderRadius: 12,
                    background: isFeatured ? c.accentBg : c.cardBg,
                    border: `1px solid ${isFeatured ? c.accentBorder : c.cardBorder}`,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <span style={{ fontSize: 11, color: c.faint, fontWeight: 500 }}>
                      {formatDate(entry.date)}
                    </span>
                    {isNew && (
                      <span
                        style={{
                          fontSize: 9, fontWeight: 700, letterSpacing: '0.06em',
                          textTransform: 'uppercase', padding: '2px 7px', borderRadius: 999,
                          background: '#ef4444', color: '#fff',
                        }}
                      >
                        {t('whatsNew.new')}
                      </span>
                    )}
                  </div>

                  <div style={{ fontSize: 14, fontWeight: 650, color: c.title, marginBottom: 4 }}>
                    {entry.title}
                  </div>
                  <div style={{ fontSize: 12.5, color: c.muted, lineHeight: 1.55 }}>
                    {entry.description}
                  </div>

                  {entry.bullets && entry.bullets.length > 0 && (
                    <ul style={{ listStyle: 'none', margin: '10px 0 0', padding: 0, display: 'flex', flexDirection: 'column', gap: 7 }}>
                      {entry.bullets.map((b, idx) => (
                        <li key={idx} style={{ display: 'flex', gap: 8, fontSize: 12.5, color: c.body, lineHeight: 1.45 }}>
                          <span
                            aria-hidden
                            style={{
                              flexShrink: 0, width: 16, height: 16, marginTop: 1, borderRadius: '50%',
                              background: c.accentBg, color: c.accent, fontSize: 10, fontWeight: 800,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}
                          >
                            ✓
                          </span>
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )
            })}
          </div>

          {/* Coming soon */}
          {upcoming.length > 0 && (
            <div style={{ marginTop: 26 }}>
              <div
                style={{
                  fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
                  color: c.faint, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6,
                }}
              >
                <span>🔮</span> {t('whatsNew.comingSoon')}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {upcoming.map((entry, i) => (
                  <div
                    key={i}
                    style={{
                      display: 'flex', alignItems: 'flex-start', gap: 10,
                      padding: '11px 14px', borderRadius: 10,
                      background: c.blueBg, border: `1px dashed ${c.blueBorder}`,
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: c.blue, marginBottom: 2 }}>
                        {entry.title}
                      </div>
                      <div style={{ fontSize: 11.5, color: c.muted, lineHeight: 1.45 }}>
                        {entry.description}
                      </div>
                    </div>
                    <span
                      style={{
                        flexShrink: 0, fontSize: 9, fontWeight: 700, letterSpacing: '0.06em',
                        textTransform: 'uppercase', padding: '2px 7px', borderRadius: 999,
                        border: `1px solid ${c.blueBorder}`, color: c.blue,
                      }}
                    >
                      {t('whatsNew.soon')}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </aside>
    </>
  )

  return (
    <>
      {/* Trigger button */}
      <button
        onClick={handleOpen}
        title={t('whatsNew.title')}
        aria-label={t('whatsNew.title')}
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 32,
          height: 32,
          borderRadius: 8,
          border: 'none',
          background: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)',
          cursor: 'pointer',
          fontSize: 15,
          transition: 'background 0.2s, transform 0.2s',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.1)' }}
        onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)' }}
      >
        ✨
        {unreadCount > 0 && (
          <span
            style={{
              position: 'absolute',
              top: -3,
              right: -3,
              minWidth: 16,
              height: 16,
              padding: '0 4px',
              borderRadius: 999,
              background: '#ef4444',
              color: '#fff',
              fontSize: 9,
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: `2px solid ${isDark ? '#050a14' : '#ffffff'}`,
              animation: 'pulse-badge 2s ease-in-out infinite',
            }}
          >
            {unreadCount}
          </span>
        )}
      </button>

      {open && createPortal(drawer, document.body)}
    </>
  )
}
